/**
 * Client Level View - Investment Strategy Hub
 * TASK-206: Multi-Portfolio Comparison and Analysis
 *
 * Shows one client's complete investment picture across ALL portfolios
 * Focus: Cross-portfolio optimization, concentration risks, duplicate holdings
 */

import PersonalizationTab from './PersonalizationTab';
import { CIOHouseView } from '../canvas/widgets/CIOHouseView';
import React, { useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  useClientFilters,
  useNavigationActions,
  useFilterActions,
} from '../../services/portfolio-refactor/store/portfolioStore';
import { useClientLevelData } from '../../hooks/useClientLevelData';
import { useClientPerformanceData } from '../../hooks/useClientPerformanceData';
import PerformanceTab from './PerformanceTab';
import RiskTab from './RiskTab';
import HistoryTab from './HistoryTab';
import { LoadingSpinner, EmptyState } from './index';
import SecondaryNav from '../layout/SecondaryNav';
import PortfolioBreakdown from './PortfolioBreakdown';
import ClientInsightsPanel from './ClientInsightsPanel';
import ConsolidatedHoldings from './ConsolidatedHoldings';
import PortfolioComparisonView from './PortfolioComparisonView';
import PortfolioOptimizationSuggestions from './PortfolioOptimizationSuggestions';
import TaxEfficientPositioning from './TaxEfficientPositioning';
import PortfolioAIChat from './PortfolioAIChat';
import PerformanceByPortfolio from './molecules/PerformanceByPortfolio';
// TASK-207: Import PortfolioFilters for Client Level filtering
import PortfolioFilters from './filters/PortfolioFilters';
import { AccountFilter } from '../../types/portfolio-views';
// TASK-277: Removed mock data imports - using real API data only
// Export functionality
import ExportDropdown from './atoms/ExportDropdown';
import { exportToCSV } from '../../utils/portfolioExport';
import { usePortfolioPptxExport } from './hooks/usePortfolioPptxExport';
import { Position as ApiPosition, CashPosition as ApiCashPosition, AssetAllocation, Transaction, portfolioApiService } from '../../services/api/portfolioApiService';

const ClientLevelView: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  // Get filters and actions from portfolioStore
  const clientFilters = useClientFilters();
  const { drillDown, setViewContext } = useNavigationActions();
  const { setClientFilters } = useFilterActions();

  // Benchmark and time range state for performance tab
  const [benchmark, setBenchmark] = useState<string>('SPI');
  const [perfTimeRange, setPerfTimeRange] = useState<string>('ytd');

  // Map PerformanceTab time ranges to API period values
  const apiPeriodMap: Record<string, '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all'> = {
    '1d': '7d', '1w': '7d', '1m': '30d', '3m': '90d',
    '6m': '90d', 'ytd': 'ytd', '1y': '1y', 'all': 'all',
  };
  const apiPeriod = apiPeriodMap[perfTimeRange] || 'ytd';

  // Sync viewContext with URL params when component mounts or clientId changes
  React.useEffect(() => {
    if (clientId) {
      setViewContext({ level: 'client', clientId, portfolioId: undefined });
    }
  }, [clientId, setViewContext]);

  // Fetch client-level data (with benchmark and period)
  const { data, loading, error } = useClientLevelData(clientId || '', clientFilters, benchmark, apiPeriod);

  // Memoized performance data transformations (used in performance tab)
  const perfData = useClientPerformanceData(data);

  // TASK-277: Fetch ALL clients for ViewLevelSelector navigation
  const { data: allClientsData } = useQuery({
    queryKey: ['all-clients-for-nav'],
    queryFn: () => portfolioApiService.getMyBookSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Resolve client name from allClientsData when the API doesn't return it
  const resolvedClientName = React.useMemo(() => {
    if (!data?.client) return '';
    // If data.client.name looks like a UUID (same as id), try to resolve from allClientsData
    if (data.client.name && data.client.name !== data.client.id) {
      return data.client.name;
    }
    if (allClientsData?.clients && clientId) {
      const match = allClientsData.clients.find(
        (c: { client_id: string; client_name: string }) => c.client_id === clientId
      );
      if (match?.client_name) return match.client_name;
    }
    return data.client.name;
  }, [data?.client, allClientsData, clientId]);

  // Transactions state
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Tab state - URL-driven via searchParams
  // TASK-829: `personalization` joins the set.
  //
  // `SignalInvestigation.tsx:104` has mapped `dna_fit_conflict -> 'personalization'` all
  // along, and `ChangeRadar`'s `CLIENT_LEVEL_TABS` allow-list did not contain it — so the
  // deep link was SILENTLY DROPPED and the advisor landed on the Client Level default. That
  // is the mechanism behind "Open full portfolio from the Amazon conflict → a page where the
  // string 'Amazon' appears nowhere".
  //
  // `PersonalizationTab` already existed and already composed `FitHeatmap` + `BeforeAfter` +
  // `DriftBars`; it was mounted only in `PortfolioDashboardPresenter`. Nothing was built here
  // — a destination that existed was made reachable.
  const VALID_TABS = ['overview', 'composition', 'performance', 'risk', 'history', 'portfolios', 'personalization', 'cio'] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.includes(searchParams.get('tab') as typeof VALID_TABS[number])
    ? searchParams.get('tab')!
    : 'overview';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Phase 3: AI Chat Assistant state
  const [highlightedSection, setHighlightedSection] = useState<string>('');
  const [scrollTarget, setScrollTarget] = useState<string>('');
  const [showCorrelationModal, setShowCorrelationModal] = useState(false);

  // Refs for scrolling
  const optimizationRef = useRef<HTMLDivElement>(null);
  const taxEfficiencyRef = useRef<HTMLDivElement>(null);
  // correlationRef removed - correlation analysis hidden, show via AI on demand

  // Handle portfolio card click (drill down to portfolio level)
  const handlePortfolioClick = (portfolioId: string) => {
    // Pass clientId explicitly to ensure it's preserved when navigating back
    drillDown('portfolio', portfolioId, clientId);
    // TASK-371: Navigate to URL-based portfolio route
    navigate(`/portfolio/client/${clientId}/portfolio/${portfolioId}`);
  };

  // Phase 3: AI Chat handlers
  const handleAITabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleAIFilterChange = (filter: any) => {
    setClientFilters(filter);
  };

  const handleAIHighlight = (section: string) => {
    setHighlightedSection(section);
  };

  const handleAIScroll = (target: string) => {
    setScrollTarget(target);
  };

  // Handle scrolling when scroll target changes
  React.useEffect(() => {
    if (!scrollTarget) return;

    const scrollToRef = () => {
      let targetRef: React.RefObject<HTMLDivElement> | null = null;

      switch (scrollTarget) {
        case 'optimization':
          targetRef = optimizationRef;
          break;
        case 'tax-efficiency':
          targetRef = taxEfficiencyRef;
          break;
        case 'correlation':
          setShowCorrelationModal(true);
          return;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(scrollToRef, 100);

    // Clear scroll target after scrolling
    setTimeout(() => setScrollTarget(''), 1000);
  }, [scrollTarget]);

  // Export handler — PPTX flows through the LLM pipeline, CSV stays local (TASK-407).
  const { loading: exportLoading, runExport } = usePortfolioPptxExport({
    clientId,
    clientName: resolvedClientName,
    onCsv: () => {
      if (!data) return;
      const exportHoldings = data.consolidatedHoldings ?? {
        positions: [], assetAllocation: {}, currencyExposure: {}, sectorExposure: {},
      };
      const positions: ApiPosition[] = (exportHoldings.positions ?? []).map((pos) => ({
        id: pos.isin,
        client_id: data.client.id,
        isin: pos.isin,
        security_name: pos.name,
        quantity: 0,
        market_price: pos.price,
        current_price: pos.price,
        market_value: pos.value,
        book_value: 0,
        unrealized_pl: pos.unrealized_pl || 0,
        currency: pos.currency || 'CHF',
        asset_class: pos.asset_class,
        sector: pos.sector,
      } as ApiPosition));
      const cashPositions: ApiCashPosition[] = [];
      const allocation: AssetAllocation = {
        by_asset_class: exportHoldings.assetAllocation ?? {},
        by_currency: exportHoldings.currencyExposure ?? {},
        by_sector: exportHoldings.sectorExposure ?? {},
        total_value: data.client.totalAuM,
      };
      exportToCSV(positions, allocation, cashPositions, data.client.totalAuM);
    },
  });

  // TASK-207: Get available accounts from client portfolios
  const availableAccounts = React.useMemo((): AccountFilter[] => {
    if (!data) return [];

    // Extract unique accounts from client portfolios
    // TODO: Replace with actual account data from API when available
    return data.portfolios.flatMap((portfolio) => [
      {
        id: `${portfolio.id}-inv`,
        name: `${portfolio.name} - Investment`,
        type: 'investment' as const,
        currency: portfolio.currency || 'CHF',
        enabled: true,
      },
    ]);
  }, [data]);

  // TASK-207: Update clientFilters.accounts when available accounts change
  React.useEffect(() => {
    if (availableAccounts.length > 0 && clientFilters.accounts.length === 0) {
      setClientFilters({ accounts: availableAccounts });
    }
  }, [availableAccounts, clientFilters.accounts.length, setClientFilters]);

  // Fetch transactions for history tab
  React.useEffect(() => {
    const fetchTransactions = async () => {
      if (!clientId) return;

      setTransactionsLoading(true);
      try {
        const response = await portfolioApiService.getTransactions(clientId, 100);
        setTransactions(response.transactions || []);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
      } finally {
        setTransactionsLoading(false);
      }
    };

    fetchTransactions();
  }, [clientId]);

  // TASK-277: Use ALL clients for ViewLevelSelector navigation (not just current)
  const clientOptions = React.useMemo(() => {
    // Use all clients from my-book summary for navigation
    if (allClientsData?.clients) {
      return allClientsData.clients.map((client: { client_id: string; client_name: string; total_value: number }) => ({
        id: client.client_id,
        name: client.client_name || client.client_id,
        totalAuM: client.total_value || 0
      }));
    }
    // Fallback to current client if all clients not loaded yet
    if (!data?.client) return [];
    return [{
      id: data.client.id,
      name: data.client.name,
      totalAuM: data.client.totalAuM
    }];
  }, [allClientsData, data]);

  const portfolioOptions = React.useMemo(() => {
    if (!data?.portfolios) return [];

    return data.portfolios.map(portfolio => ({
      id: portfolio.id,
      name: portfolio.name,
      clientId: clientId || '',
      value: portfolio.value
    }));
  }, [data, clientId]);

  // TASK-877: `Compare` is offered only when there is something to compare.
  //
  // The tab renders a selector captioned "Select portfolios to compare (select 2 or more)"
  // over a list that has one entry. Measured on 2026-08-30: every client in the book
  // returns `count: 1` from `/clients/{id}/portfolios` — Schneider has 15 rows in
  // `portfolios` but 14 are inactive, so the advisor is given an instruction that cannot
  // be satisfied and a tab that can never produce output.
  //
  // Hidden rather than deleted: `PortfolioComparisonView` is complete and correct, and
  // works the moment a client holds two active portfolios. What was wrong was offering it
  // to clients who hold one.
  const comparablePortfolioCount = (data?.portfolios ?? []).length;
  const canCompare = comparablePortfolioCount >= 2;

  // Tab configuration - matches Portfolio Dashboard structure
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'composition', label: 'Composition' },
    { id: 'performance', label: 'Performance' },
    { id: 'risk', label: 'Risk' },
    { id: 'history', label: 'History' },
    ...(canCompare ? [{ id: 'portfolios', label: 'Compare' }] : []),
    { id: 'personalization', label: 'Personalization' },
    // TASK-831: there was no CIO surface anywhere in the product, while `cio_stale_sell`
    // asked the advisor to act on the house view and every swap candidate is a CIO BUY.
    { id: 'cio', label: 'House view' },
  ];

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SecondaryNav />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SecondaryNav />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Error Loading Client Data
            </h3>
            <p className="text-red-600">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  // Render empty state
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SecondaryNav />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <EmptyState
            type="no-data"
            title="Client Not Found"
            description="The requested client could not be found."
          />
        </main>
      </div>
    );
  }

  // Render active tab content
  const renderTabContent = () => {
    // Check if client has portfolios - show empty state for all tabs
    if (!data.portfolios || data.portfolios.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Portfolio Data</h3>
            <p className="text-gray-600">
              This client doesn't have any portfolios yet. Create a portfolio to see data here.
            </p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      // TASK-829: the tab `dna_fit_conflict` deep-links to. Reuses the existing component
      // rather than a second composition of the same three widgets.
      case 'personalization':
        // TASK-832/830: the signal's focus rides in the URL beside the tab, so a deep link
        // opens ON the breach or the conflicting instrument rather than on the whole
        // allocation. TASK-726's contract: the address is what restores the context.
        return (
          <PersonalizationTab
            clientId={clientId}
            focusSac={searchParams.get('focusSac') ?? undefined}
            focusIsin={searchParams.get('focusIsin') ?? undefined}
          />
        );
      // TASK-831: the same widget the canvas mounts — build once, mount twice, which
      // `PersonalizationTab` established as the pattern.
      case 'cio':
        return (
          <div className="bg-white rounded-lg shadow p-4">
            {/* `clientId` is optional on this route; the widget needs one to have holdings
                to rate, so an absent id gets the honest empty rather than an empty list. */}
            {clientId ? (
              <CIOHouseView
                clientId={clientId}
                focusIsin={searchParams.get('focusIsin') ?? undefined}
              />
            ) : (
              <p className="text-sm text-gray-600">Select a client to read the house view.</p>
            )}
          </div>
        );
      case 'overview': {
        // TASK-277: Add null safety for insights and risk data
        const insightsData = data.insights ?? { concentrationRisks: [], diversificationGaps: [], duplicateHoldings: [], recommendations: [] };
        const overviewRisk = data.risk?.combined ?? { sharpeRatio: 0, volatility: 0, var1day: 0 };

        // Format currency for display
        const formatCurrency = (value: number) => {
          return new Intl.NumberFormat('de-CH', {
            style: 'currency',
            currency: 'CHF',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value);
        };

        // TASK-654: unavailable P&L renders as unavailable, never CHF 0.
        const unrealizedPL = data.performance?.totalGains ?? null;
        const sharpeRatio = overviewRisk.sharpeRatio;

        return (
          <div className="space-y-6">
            {/* Client Summary - Simplified */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Unrealized P&L */}
                <div>
                  <p className="text-sm text-gray-600 mb-1">Unrealized P&L</p>
                  <p
                    className={`text-2xl font-bold ${
                      unrealizedPL == null
                        ? 'text-gray-400'
                        : unrealizedPL >= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                    }`}
                  >
                    {unrealizedPL == null
                      ? 'Not available'
                      : `${unrealizedPL >= 0 ? '+' : ''}${formatCurrency(unrealizedPL)}`}
                  </p>
                </div>

                {/* Sharpe Ratio */}
                <div>
                  <p className="text-sm text-gray-600 mb-1">Sharpe Ratio</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {sharpeRatio !== null && sharpeRatio !== undefined ? sharpeRatio.toFixed(2) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Portfolio Breakdown - grid of portfolio cards */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Portfolios ({data.portfolios.length})
              </h3>
              <PortfolioBreakdown
                portfolios={data.portfolios}
                onPortfolioClick={handlePortfolioClick}
              />
            </div>

            {/* Client Insights Panel */}
            <div className={`bg-white rounded-lg shadow p-6 transition-all ${
              highlightedSection === 'concentration-risks' || highlightedSection === 'currency-risk'
                ? 'ring-4 ring-blue-400'
                : ''
            }`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Client-Level Insights
              </h3>
              <ClientInsightsPanel insights={insightsData} />
            </div>

            {/* Phase 3: Portfolio Optimization Suggestions */}
            <div ref={optimizationRef} className="bg-white rounded-lg shadow p-6">
              {/*
                TASK-722: `?? []` used to turn "the API never sent this" into "the analysis
                found nothing", which the component then rendered as a green "Portfolio is
                Well Optimized". `optimizationScenarios` is assigned by no backend, so that
                verdict was shown to every client, always, on the basis of no analysis — and
                it is the surface that contradicts Composition's "critical rebalancing
                required". The status is now derived from what the API actually proved.
              */}
              <PortfolioOptimizationSuggestions
                clientId={data.client.id}
                scenarios={data.optimizationScenarios ?? []}
                analysisStatus={
                  data.optimizationScenarios === undefined ? 'unavailable' : 'complete'
                }
                totalAuM={data.client.totalAuM}
              />
            </div>

            {/* Phase 3: Tax-Efficient Positioning */}
            {data.taxEfficiency && (
              <div ref={taxEfficiencyRef} className="bg-white rounded-lg shadow p-6">
                <TaxEfficientPositioning
                  clientId={data.client.id}
                  taxLossOpportunities={data.taxEfficiency.taxLossOpportunities}
                  gainCoordinationStrategies={data.taxEfficiency.gainCoordinationStrategies}
                  dividendRecommendations={data.taxEfficiency.dividendRecommendations}
                  estimatedTotalTaxSavings={data.taxEfficiency.estimatedTotalTaxSavings}
                />
              </div>
            )}

            {/* Phase 3: Portfolio Correlation Analysis - HIDDEN by default, show via AI on demand */}
            {/* Users can ask AI: "Show me portfolio correlation analysis" to see this data */}
            {/*
            <div ref={correlationRef} className="bg-white rounded-lg shadow p-6">
              <PortfolioCorrelationAnalysis
                clientId={data.client.id}
                correlationMatrix={data.correlationAnalysis.correlationMatrix}
                diversificationBenefits={data.correlationAnalysis.diversificationBenefits}
                averageCorrelation={data.correlationAnalysis.averageCorrelation}
                effectiveDiversification={data.correlationAnalysis.effectiveDiversification}
              />
            </div>
            */}
          </div>
        );
      }

      case 'composition': {
        // TASK-277: Add null safety for consolidated holdings
        const compositionHoldings = data.consolidatedHoldings ?? { positions: [], totalValue: 0, assetAllocation: {}, currencyExposure: {}, sectorExposure: {} };
        return (
          <div className="space-y-6">
            {/* Export Button */}
            <div className="flex justify-end">
              <ExportDropdown
                onExport={runExport}
                disabled={loading || !compositionHoldings.positions.length}
                loading={exportLoading}
              />
            </div>

            {/* Phase 2: Full Consolidated Holdings Component */}
            <ConsolidatedHoldings
              data={compositionHoldings}
              // TASK-723: needed to reach the canonical drift classification. Without it the
              // view shows no verdict rather than one derived from a hardcoded band.
              clientId={clientId}
            />
          </div>
        );
      }

      case 'performance': {
        // CLIENT LEVEL PERFORMANCE - uses memoized hook data (useClientPerformanceData)
        const perfByPortfolio = data.performance?.byPortfolio ?? [];

        return (
          <div className="space-y-6">
            <PerformanceTab
              mode="client"
              metrics={perfData.metrics}
              history={perfData.history}
              attribution={perfData.attribution}
              topContributors={perfData.topContributors}
              bottomContributors={perfData.bottomContributors}
              portfolioSeries={perfData.portfolioSeries}
              valueFormat="percentage"
              loading={false}
              benchmark={benchmark}
              onBenchmarkChange={setBenchmark}
              timeRange={perfTimeRange}
              onTimeRangeChange={setPerfTimeRange}
              clientId={clientId}
            />

            {/* ═══════════════════════════════════════════════════════════════
                PORTFOLIO BREAKDOWN - CLIENT-SPECIFIC COMPONENT
                Shows WHICH portfolios drive the consolidated performance above
                This is additive context, not part of the main Performance Tab
                ═══════════════════════════════════════════════════════════════ */}
            <PerformanceByPortfolio
              portfolios={perfByPortfolio.map(p => ({
                id: p.id,
                name: p.name,
                // TASK-897: the nulls pass THROUGH. `?? 0` here is why `Performance by
                // Portfolio` ranked `+0.00%` best and `+0.00%` worst and colour-coded both,
                // naming the same portfolio for each — the Top Contributors defect one card
                // lower. `useClientLevelData` already types these nullable and returns null
                // when the value is unknown; a renderer cannot defend against a value that
                // was replaced before it arrived, which is TASK-872's whole lesson.
                ytd: p.ytd ?? null,
                contributionToTotal: p.contributionToTotal ?? null,
                volatility: p.volatility ?? null
              }))}
              onPortfolioClick={handlePortfolioClick}
              showDetails={false}
            />
          </div>
        );
      }

      case 'risk': {
        // TASK-277: Add null safety for risk data
        const riskData = data.risk?.combined ?? { volatility: 0, sharpeRatio: 0, var1day: 0 };
        const concentrationRisks = data.insights?.concentrationRisks ?? [];
        const riskByPortfolio = data.risk?.byPortfolio ?? [];

        // Map consolidated positions to the shape RiskContributorsTable expects
        const riskPositions = (data.consolidatedHoldings?.positions ?? []).map((p) => ({
          isin: p.isin,
          security_name: p.name,
          market_value: p.value,
          asset_class: p.asset_class,
          currency: p.currency,
          country: p.country,
        }));

        return (
          <div className="space-y-6">
            {/* Risk Tab - pass aggregated data + positions */}
            <RiskTab
              mode="client"
              clientId={data.client.id}
              riskMetrics={{
                // TASK-897: absent stays absent. `beta ?? 1.0` is where the placeholder
                // `Beta 1.00` came from — a number the RM reads as a measurement of market
                // sensitivity, invented by this line. `sharpe_ratio ?? 0` is why Sharpe read
                // `N/A` on Overview and `0.00` on Risk: two surfaces, one metric, two answers,
                // and the disagreement was manufactured here rather than in either renderer.
                // `RiskMetricCard` renders `—` for null since TASK-872.
                volatility: riskData.volatility ?? null,
                sharpe_ratio: riskData.sharpeRatio ?? null,
                var_95: riskData.var1day ?? null,
                var_1day: riskData.var1day ?? null,
                var_10day: riskData.var1day == null ? null : riskData.var1day * 3.16,
                beta: riskData.beta ?? null,
                concentration_risk: concentrationRisks.length > 0 ? 0.7 : 0.3,
              }}
              stressTests={data.stressTests}
              positions={riskPositions}
              portfolioValue={data.client.totalAuM}
              performanceHistory={data.performance?.history}
              loading={false}
            />

            {/* Risk breakdown by portfolio */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Risk by Portfolio
              </h3>
              <div className="space-y-4">
                {riskByPortfolio.map((pf) => (
                  <div key={pf.id} className="border-l-4 border-yellow-500 pl-4">
                    <div className="font-medium">{pf.name}</div>
                    <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                      <div>
                        <span className="text-gray-600">Volatility:</span>
                        <span className="ml-2 font-semibold">{(pf.volatility ?? 0).toFixed(2)}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">VaR (1d):</span>
                        <span className="ml-2 font-semibold">CHF {((pf.var1day ?? 0) / 1000).toFixed(0)}K</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Sharpe:</span>
                        <span className="ml-2 font-semibold">{(pf.sharpeRatio ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'history':
        return (
          <div className="space-y-6">
            {/* History Tab - shows transactions across all portfolios */}
            <HistoryTab
              clientId={data.client.id}
              transactions={transactions}
              loading={transactionsLoading}
            />
          </div>
        );

      case 'portfolios': {
        // TASK-877: the tab is hidden below 2 portfolios, but `activeTab` is read from
        // `?tab=` — a bookmarked or shared URL reaches this branch regardless. Say why
        // there is nothing here rather than rendering "select 2 or more" over one row.
        if (!canCompare) {
          return (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Nothing to compare
              </h3>
              <p className="text-sm text-gray-600">
                {comparablePortfolioCount === 1
                  ? 'This client holds a single portfolio. Comparison needs two or more.'
                  : 'This client holds no portfolios to compare.'}
              </p>
            </div>
          );
        }
        // TASK-277: Add null safety for portfolios tab data
        const portfoliosData = data.portfolios ?? [];
        const comparisonHoldings = data.consolidatedHoldings ?? { positions: [], totalValue: 0, assetAllocation: {}, currencyExposure: {}, sectorExposure: {} };
        return (
          <div className="space-y-6">
            {/* Phase 2: Full Portfolio Comparison View */}
            <PortfolioComparisonView
              clientId={clientId || ''}
              portfolios={portfoliosData}
              selectedPortfolios={clientFilters.comparePortfolios || []}
              consolidatedHoldings={comparisonHoldings}
              onSelectPortfolios={(selected: string[]) => {
                setClientFilters({ comparePortfolios: selected });
              }}
            />
          </div>
        );
      }

      default:
        return (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-600">Tab content not yet implemented</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <SecondaryNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Client Header */}
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#002B45]">{resolvedClientName}</h1>
              <div className="flex gap-6 mt-3 text-sm text-gray-600">
                {data.client.lastContact && (
                  <div>
                    <span className="font-medium">Last Contact:</span> {new Date(data.client.lastContact).toLocaleDateString('de-CH')}
                  </div>
                )}
                {data.client.nextMeeting && (
                  <div>
                    <span className="font-medium">Next Meeting:</span> {new Date(data.client.nextMeeting).toLocaleDateString('de-CH')}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-[#002B45]">
                CHF {((data.client.totalAuM ?? 0) / 1000000).toFixed(2)}M
              </div>
              <div className="text-sm text-gray-600">Total AuM</div>
              <div className="text-sm text-gray-600 mt-2">
                {(data.portfolios ?? []).length} {(data.portfolios ?? []).length === 1 ? 'Portfolio' : 'Portfolios'}
              </div>
            </div>
          </div>
        </div>

        {/* TASK-207: Portfolio Filters for Client Level View */}
        {availableAccounts.length > 0 && (
          <PortfolioFilters
            filters={clientFilters}
            availableAccounts={availableAccounts}
            onFiltersChange={(partialFilters) => {
              setClientFilters(partialFilters);
            }}
            historicalSupported={false} // TODO: Enable when backend supports historical snapshots
            compact={false}
            clients={clientOptions}
            portfolios={portfolioOptions}
          />
        )}

        {/* Tab Navigation */}
        <div className="mb-6 bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-[#002B45] text-[#002B45]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </main>

      {/* Phase 3: AI Portfolio Assistant - Floating Chat Button */}
      <PortfolioAIChat
        clientData={data}
        onTabChange={handleAITabChange}
        onFilterChange={handleAIFilterChange}
        onHighlight={handleAIHighlight}
        onScroll={handleAIScroll}
      />

      {/* Correlation Analysis Modal */}
      {showCorrelationModal && data?.correlationMatrix && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Portfolio Correlation Analysis</h2>
              <button
                onClick={() => setShowCorrelationModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {(() => {
                const matrix = data.correlationMatrix;
                const assets = Object.keys(matrix);
                if (assets.length === 0) {
                  return <p className="text-gray-500">No correlation data available.</p>;
                }
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left py-2 px-3 text-gray-600 font-medium border-b border-gray-200" />
                          {assets.map((asset) => (
                            <th key={asset} className="text-center py-2 px-3 text-gray-600 font-medium border-b border-gray-200 truncate max-w-[100px]" title={asset}>
                              {asset.length > 12 ? `${asset.slice(0, 12)}…` : asset}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {assets.map((rowAsset) => (
                          <tr key={rowAsset} className="border-b border-gray-100">
                            <td className="py-2 px-3 font-medium text-gray-900 truncate max-w-[120px]" title={rowAsset}>
                              {rowAsset.length > 15 ? `${rowAsset.slice(0, 15)}…` : rowAsset}
                            </td>
                            {assets.map((colAsset) => {
                              const val = matrix[rowAsset]?.[colAsset] ?? 0;
                              const absVal = Math.abs(val);
                              const bg = rowAsset === colAsset
                                ? 'bg-gray-100'
                                : absVal > 0.7
                                  ? val > 0 ? 'bg-red-100' : 'bg-blue-100'
                                  : absVal > 0.4
                                    ? val > 0 ? 'bg-red-50' : 'bg-blue-50'
                                    : '';
                              return (
                                <td key={colAsset} className={`text-center py-2 px-3 ${bg}`}>
                                  {typeof val === 'number' ? val.toFixed(2) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientLevelView;
